<template>
	<li
		:id="`location-card-${location.mmsClubId}`"
		class="result-card border-bottom mx-n2 mx-md-0 p-2 d-md-flex gap-2"
		:class="{ selected: isSelected }"
		tabindex="0"
		:data-testid="`location-card-${location.mmsClubId}`"
	>
		<picture v-if="xsImage && smImage">
			<source
				media="(min-width:992px) and (max-width: 1300px)"
				data-testid="thumbnail-xs"
				:srcset="xsImage.path"
			/>
			<img
				v-if="useDynamicMedia"
				:alt="smImage.name"
				data-testid="thumbnail-sm"
				:src="smImage.path"
				class="result-image rounded-3 mb-1 mb-md-0"
			/>
			<img
				v-else
				:alt="isMobileMapView ? xsImage.name : smImage.name"
				data-testid="thumbnail-sm"
				:src="isMobileMapView ? xsImage.path : smImage.path"
				class="result-image rounded-3 mb-1 mb-md-0"
			/>
		</picture>

		<div class="result-body flex-grow-1 d-flex flex-column justify-content-between">
			<div>
				<div class="result-title h3 mb-1">{{ location.clubTitle }}</div>
				<div class="small opacity-75">( {{ location.distance }} mi )</div>
				<div v-if="location.comingSoon" class="mt-2" data-testid="coming-soon">
					<span class="badge rounded-pill border px-2">{{ location.comingSoon }}</span>
				</div>
				<div v-if="location.locationMessage" class="mt-2" data-testid="location-message">
					<span class="badge rounded-pill border px-2">{{ location.locationMessage }}</span>
				</div>
			</div>

			<div class="d-flex justify-content-between align-items-baseline">
				<div class="small opacity-75">
					<template v-if="isWaitlist">
						<a
							v-if="location.waitlistURL"
							v-adobe-data-layer="waitlistADLConfig"
							:href="props.location.waitlistURL"
							data-testid="waitlist-link"
						>
							{{ waitlistCtaText }}
						</a>
					</template>
					<template v-else-if="hasJoinLink">
						<a v-adobe-data-layer="joinADLConfig" :href="joinLink" data-testid="oms-link">
							<span class="visually-hidden">Join {{ location.clubTitle }}, </span>
							Starting at {{ location.startingPriceString }}/mo
						</a>
					</template>
				</div>
				<a
					v-adobe-data-layer="detailsADLConfig"
					:href="location.prospectPagePath"
					class="btn btn-dark btn-sm px-2 px-sm-3 px-lg-2 px-xl-3"
				>
					Details <span class="visually-hidden">about {{ location.clubTitle }}</span>
				</a>
			</div>
		</div>
	</li>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, type Ref } from 'vue';
import {
	featureFlagService,
	sessionService,
	storageService
} from '@life-time-inc/enterprise-framework-core';
import { getAuthoredContent } from '@/util/getAuthoredContent';
import type { Image, Location } from 'Types/aemui/find-a-location/findaLocation';
import vAdobeDataLayer from '@/directives/AdobeDataLayer';

export interface LocationCardContent {
	openWaitlistCta: string;
	waitlistCta: string;
}

const props = defineProps<{
	location: Location;
	isSelected: boolean;
	isMobileMapView: boolean;
}>();

const authoredContent: Ref<LocationCardContent> = ref({
	openWaitlistCta: 'Become a Member',
	waitlistCta: 'Join the Waitlist'
});

onMounted(() => {
	authoredContent.value = {
		...authoredContent.value,
		...getAuthoredContent<LocationCardContent>('aemuiFindALocationScript')
	};
});

const useDynamicMedia = featureFlagService.isEnabled('aemuiDynamicMediaLocationImages');

const findImage = (ratio: string, sizeName: string): Image | undefined => {
	const image = useDynamicMedia
		? props.location.images?.find((image) => image?.ratio === ratio)
		: props.location.images?.find((image) => image.path.includes(sizeName));
	if (!image) {
		return;
	}
	if (!useDynamicMedia) {
		image.path = image?.path.includes('https://www.lifetime.life')
			? image?.path
			: `https://www.lifetime.life${image?.path}`;
	}
	return image;
};

const xsImage = computed(() => findImage('1x1', '-xs.'));
const smImage = computed(() => findImage('16x9', '-sm.'));

const hasJoinLink = computed(() => {
	const { clubTypeId, oms, open } = props.location;
	return open === 'open' && oms === 'Yes' && (clubTypeId === 1 || clubTypeId === 2);
});

const joinLink = computed(() => {
	const memberReferralCookieName = 'memberReferral';
	const cookieCorpMembership = 'corpMembership';
	const promotionURLParamConst = 'promotionName';

	// get member referral information
	let referralCode = '';
	let referralSource = '';
	const storedReferralInfo = sessionService.getCookie(memberReferralCookieName);
	if (
		storedReferralInfo !== null &&
		typeof storedReferralInfo !== 'undefined' &&
		storedReferralInfo.length > 0
	) {
		referralCode = storedReferralInfo.split('|')[0];
		referralSource = storedReferralInfo.split('|')[1];
	}

	// gather promotion information
	let promotionName = '';
	const url = new URL(window.location.href);
	const promotionURLParam = url.searchParams.get(promotionURLParamConst);

	const storedPromotionInfo = sessionService.getCookie(cookieCorpMembership);

	if (promotionURLParam !== null) {
		promotionName = promotionURLParam;
		storageService.sessionSet(cookieCorpMembership, `${promotionName}|LTA-YTT`);
	} else if (
		storedPromotionInfo !== null &&
		typeof storedPromotionInfo !== 'undefined' &&
		storedPromotionInfo.length > 0
	) {
		promotionName = storedPromotionInfo.split('|')[0];
	}

	// get utm data
	const utmData = storageService.sessionGet('utm_params');

	// set join link query parameters
	const queryString = new URLSearchParams();
	queryString.append('clubId', String(props.location.mmsClubId));

	if (referralCode) {
		queryString.append('source', referralSource);
		queryString.append('referral', encodeURIComponent(referralCode));
	}

	if (promotionName) {
		queryString.append('promotionName', promotionName);
		queryString.append('source', 'web');
	}

	if (utmData) {
		// From this URL (https://www.lifetime.life/life-time-locations/mn-minnetonka.html?utm_source=facebook&utm_medium=local&utm_campaign=facebook-local-club)
		// utmData is observed to be as follows
		// {utm_source: ["facebook"], utm_medium: ["local"], utm_campaign: ["facebook-local-club"]}
		for (const [key, value] of Object.entries(utmData)) {
			if (Array.isArray(value)) {
				value.forEach((val: string) => queryString.append(key, val));
			} else {
				// handle the case where it isn't an array, just in case
				queryString.append(key, value);
			}
		}
	}

	const path = window.lt.globalPageDrivers.global.transition;

	return `${path}?${queryString}`;
});

const isWaitlist = computed(() => props.location.webPreSaleWaitlist === '1');

const waitlistCtaText = computed(() => {
	return props.location.open === 'open'
		? authoredContent.value.openWaitlistCta
		: authoredContent.value.waitlistCta;
});

const detailsADLConfig = {
	name: 'findlocation',
	type: 'link',
	title: 'Details',
	link: props.location.prospectPagePath
};

const joinADLConfig = {
	name: 'findlocation',
	type: 'link',
	title: `Starting at ${props.location.startingPriceString}/mo`,
	link: joinLink.value
};

const waitlistADLConfig = {
	name: 'findlocation',
	type: 'link',
	title: 'Join the Waitlist',
	link: props.location.waitlistURL
};
</script>
